import { useEffect, useContext } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import ReducersIndex from "reduxModules/ducks";
import Button from "components/molecules/Button";
import { buttonThemes } from "globalConstants/Themes";
import { getCurrentValue2, getRequestFromData } from "reduxModules/ducks/container";
import ToolContext from "components/services/ToolContext";

const HUBSPOT_FORM_CONFIG = {
  region: "na2",
  portalId: "45958592",
  formId: "c431cede-783c-4e8f-92a7-2cab2640a368",
};

/**
* @module GetQuote
* @description Handles HubSpot V4 form integration for quote requests.
* 
* @component
* @param {Object} props
* @param {Function} props.setSilentSave - Function to trigger silent project save
*/
const GetQuote = (props) => {
  const { tool: { fullName: toolName } } = useContext(ToolContext).constants;
  const dispatch = useDispatch();
  const store = useStore();

  // const isModalOpen = useSelector(state => getCurrentValue2(state, 'isModalOpen'));
  const isRequestQuoteOpen = useSelector(state =>
    state.ui.modals.find(m => m.modalName === 'requestQuote')?.modalOpen ?? false
  );

  const submitRequest = useSelector(state => getCurrentValue2(state, 'submitRequest'));
  const requestedEquipment = useSelector(getRequestFromData);
  const project = useSelector(state => state.container.project);

  // Clean up and reinject form when modal opens/closes
  useEffect(() => {
    const container = document.getElementById('hs-form-container');
    if (!container) return;

    if (isRequestQuoteOpen) {
      // Register on-ready listener BEFORE injecting the div
      window.addEventListener("hs-form-event:on-ready", function onFormReady(event) {
        window.removeEventListener("hs-form-event:on-ready", onFormReady);

        // Store form instance for message field injection
        if (window.HubSpotFormsV4) {
          window._hsFormInstance = window.HubSpotFormsV4.getFormFromEvent(event);
        };

        // Pre-populate fields after HubSpot finishes its internal render
        setTimeout(function () {
          const setField = function (name, value) {
            const input = document.querySelector(`#hs-form-container input[name='${name}']`);
            if (input && value) {
              const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype, 'value'
              ).set;
              nativeInputValueSetter.call(input, value);
              input.dispatchEvent(new Event('input', { bubbles: true }));
              input.dispatchEvent(new Event('change', { bubbles: true }));
            };
          };

          if (project.name) setField("0-1/design_tool_project_name", project.name);
          if (project.company) setField("0-1/company", project.company);

          if (project.designer) {
            const nameParts = project.designer.trim().split(/\s+/);
            const firstName = nameParts[0] || "";
            const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
            setField("0-1/firstname", firstName);
            setField("0-1/lastname", lastName);
          };
        }, 300);
      });

      // Register success listener
      window.addEventListener("hs-form-event:on-submission:success", function onQuoteSuccess() {
        console.log("Quote request successfully submitted to HubSpot");
        window.removeEventListener("hs-form-event:on-submission:success", onQuoteSuccess);
        dispatch(ReducersIndex.container.setRequestedQuote(true));
        dispatch(ReducersIndex.uiReducers.closeModal('requestQuote'));
        dispatch(ReducersIndex.uiReducers.clearModals());
      });

      // Inject fresh form div — V4 self-initializes
      container.innerHTML = '';
      const formDiv = document.createElement('div');
      formDiv.className = 'hs-form-html';
      formDiv.setAttribute('data-region', HUBSPOT_FORM_CONFIG.region);
      formDiv.setAttribute('data-form-id', HUBSPOT_FORM_CONFIG.formId);
      formDiv.setAttribute('data-portal-id', HUBSPOT_FORM_CONFIG.portalId);
      container.appendChild(formDiv);

    } else {
      // Clean up on modal close
      container.innerHTML = '';
      window._hsFormInstance = null;
    };
  }, [isRequestQuoteOpen]);

  // Handle form submission
  useEffect(() => {
    if (!submitRequest) return;

    const handleSubmit = async () => {
      const isTest = ['localhost', '127.0.0.1'].includes(window.location.hostname);
      const currentRev = store.getState().ui.revision;

      // Save first
      props.setSilentSave(true);

      // Wait for save to complete
      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
      const currentSavekey = store.getState().ui.savekey;
      let attempts = 0;

      let newRev;
      while (attempts < 50) {
        await delay(100);
        newRev = store.getState().ui.revision;
        const newSavekey = store.getState().ui.savekey;
        // Break if revision changed OR savekey appeared for first time
        if (newRev !== currentRev || newSavekey !== currentSavekey) break;
        attempts++;
      };

      // Now URL reflects the saved state
      const url = window.location.href;
      let text = isTest ? `This is a test (rev:${newRev}) - ` : "";
      text += `Customer requesting a quote for a system designed with ${toolName}.\n`;
      text += `Design URL: ${url}\n`;
      text += `Requested products (per area/room):\n`;
      text += requestedEquipment.join('\n');

      // console.log("=== QUOTE PAYLOAD ===");
      // console.log(text);
      // console.log("=== END PAYLOAD ===");

      // Inject equipment list into message field
      // Set message field via both V4 API and DOM — both needed
      if (window._hsFormInstance) {
        window._hsFormInstance.setFieldValue("0-1/message", text);
      };
      const messageInput = document.querySelector(
        "#hs-form-container input[name='0-1/message'], #hs-form-container textarea[name='0-1/message']"
      );
      if (messageInput) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype, 'value'
        ).set;
        nativeInputValueSetter.call(messageInput, text);
        messageInput.dispatchEvent(new Event('input', { bubbles: true }));
        messageInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        console.warn("HubSpot message field not found");
      };

      // Trigger native submit button
      const submitBtn = document.querySelector("#hs-form-container button[type='submit']");
      if (submitBtn) {
        submitBtn.click();
      } else {
        console.warn("HubSpot submit button not found");
      };

      dispatch(ReducersIndex.uiReducers.setSubmitRequest(false));
    };

    handleSubmit();
  }, [submitRequest]);

  return (
    <Button
      id={'getQuote'}
      theme={buttonThemes.nav_red}
      label={"REQUEST QUOTE"}
      onClick={() => dispatch(ReducersIndex.uiReducers.openModal('requestQuote'))}
    />
  );
};

export default GetQuote;